import type { FC } from 'react';
import type { SalesItem, Stripe, BookBaseColor } from 'shared/types/index';
import type { FirstInputType } from './First';

import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import { loadStripe as LoadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import { Wrapper } from '../Common/Wrapper';
import { First } from './First';
import { Second } from './Second';

import useSales from 'shared/hooks/useSales';
import useErrorLogger from 'shared/hooks/useErrorLogger';
import useWebsiteEditor from 'shared/hooks/useWebsiteEditor';

import { ApiService } from 'shared/helpers/api';
import { COLOR_BLUE_6 } from 'shared/helpers/colors';
import { shade, sanitizeRichText, firePixelEvent } from 'shared/helpers/utils';
import { defaultWebValue, filterBackgroundColor } from 'shared/helpers/website';

import 'react-phone-number-input/style.css';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import HeadlineOnly from '../HeadlineOnly';
import { stateCodes, TOP_USED_CURRENCIES } from 'shared/helpers/static';
import useSalesForm from 'shared/hooks/useSalesForm';

const Container = styled.div<{ backgroundColor: string }>`
  background: ${(props) => props.backgroundColor};
  padding: 95px 0;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    padding: 62px 0 60px;
  `}
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const FormWrapper = styled.div`
  position: relative;
  width: 1064px;
  padding: 52px 115px 72px;
  margin: 0 auto;
  word-wrap: break-word;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 100%;
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    padding: 52px 75px 72px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    padding: 52px 35px 72px;
  `}
`;

const ArrowImg = styled.svg`
  position: absolute;
  right: -2%;
  top: -6%;
  width: 109px;
  height: 200px;
  z-index: 3;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    right: 3%;
    top: -10%;
    width: 94px;
    height: 188px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    right: 3%;
    top: -10%;
    width: 59px;
    height: 108px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    right: 4%;
    top: -10%;
    width: 49px;
    height: 90px;
  `}
`;

const TitleItemForm = styled.h2<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $headlineFont } }) => $headlineFont};
  font-weight: 700;
  line-height: 1.35em;
  margin-bottom: 10px;
  position: relative;
  z-index: 4;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    margin-bottom: 12px;
  `}
  ${({ isEmpty, editable }) =>
    isEmpty && editable
      ? `
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #000;
      opacity: 0.9;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const DescriptionTitleForm = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  line-height: 1.35em;
  position: relative;
  z-index: 4;
  ${({ isEmpty, editable }) =>
    isEmpty && editable
      ? `
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #000;
      opacity: 0.9;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const FormBg = styled.div`
  width: 100%;
  height: 100%;
  background: #fff;
  // opacity: 0.75;
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 10px;
`;

export interface FormProps extends SalesItem {
  backgroundColor?: string;
  loadStripe?: boolean;
  type: string;
  stripe?: Stripe;
  sub_domain: string;
  deliveryToken?: string;
  navigate?: (to: string) => void;
  textColor: BookBaseColor;
  customBackgroundColor?: string;
  oppositeBackgroundColor?: string;
}

export const V1: FC<FormProps> = (props) => {
  const {
    button = defaultWebValue,
    title = defaultWebValue,
    content = defaultWebValue,
    backgroundColor = COLOR_BLUE_6,
    type,
    stripe,
    sub_domain,
    textColor,
    meta,
    customBackgroundColor,
    oppositeBackgroundColor
  } = props;

  const [firstPhase, setFirstPhase] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [stripeConfig, setStripeConfig] = useState({ id: '', clientSecret: '' });
  const [customerData, setCustomerData] = useState<FirstInputType | null>();

  const { updateActiveElement, resetActiveElement, containerClassname, editable } =
    useWebsiteEditor();
  const {
    updateDownloadUrl,
    salesType,
    price,
    eventDetails,
    salesOrderCount,
    isSubscription,
    isUpsellVIP,
    upsellDetails,
    upsellPrice
  } = useSales();
  const { logError } = useErrorLogger();

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');

  const { navigateToThankYou } = useSalesForm();

  const [orderData, setOrderData] = useState<FirstInputType>({
    phone: '',
    email: '',
    first_name: '',
    last_name: '',
    subscribe: isSubscription,
    city: '',
    street: '',
    apartment: '',
    zipcode: '',
    state: ''
  });
  const [isVip, setIsVip] = useState(false);

  const stripePromise = useMemo(() => {
    if ((type === 'sales' || isVip) && stripe)
      return LoadStripe(stripe.public_key, { stripeAccount: stripe.account });
    return null;
  }, [type, isVip, stripe]);

  const subHeadline = useMemo(() => {
    if (salesType === 'sales' || isVip) {
      return 'We will email you the files after your purchase is complete.';
    }
    return 'It will be headed your way soon, just give us your email address.';
  }, [isVip, salesType]);

  const handleFirst = async (values: FirstInputType) => {
    try {
      setIsLoading(true);

      const res = await ApiService.post(`/sales/page/${sub_domain}/order/`, {
        ...values,
        is_vip: values?.is_vip || isUpsellVIP
      });
      setOrderData({
        ...values,
        ...res.data.book_order,
        state: stateCodes[values.state as keyof typeof stateCodes] || values.state
      });
      if (res.data?.book_order?.type === 'amazon') {
        navigateToThankYou();
      }
      const clientSecret = res.data.client_secret;
      const id = String(res.data.book_order.uuid);
      setCustomerData(values);

      setStripeConfig({
        id,
        clientSecret
      });
      if (values.email) firePixelEvent('Lead', values.email);
      if (
        (price === '0' || Number(price) === 0) &&
        res.data.book_order.status === 'delivered' &&
        !res.data.book_order.is_vip
      ) {
        const response = await ApiService.get(`/sales/page/${sub_domain}/delivery/${id}/`);

        updateDownloadUrl(response.data.file_url);
        navigateToThankYou();
      } else {
        setFirstPhase(false);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errors = error.response?.data?.errors;
      if (errors && errors.length > 0 && errors[0].code === 'BOOKORDER_ERROR_0001') {
        navigateToThankYou();
      } else {
        logError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSecond = () => {
    try {
      setIsLoading(true);

      if (customerData?.email) firePixelEvent('Purchase', customerData.email);

      const checkDeliveryStatus = setInterval(async () => {
        const params = {
          email: customerData?.email,
          uuid: stripeConfig.id,
          sub_domain
        };
        const status = await ApiService.get(`/sales/page/${sub_domain}/status/`, { params });

        if (status.data.status === 'delivered') {
          clearInterval(checkDeliveryStatus);
          const response = await ApiService.get(
            `/sales/page/${sub_domain}/delivery/${stripeConfig.id}/`
          );

          updateDownloadUrl(response.data.file_url);
          setIsLoading(false);
          navigateToThankYou(stripeConfig.id);
          setFirstPhase(false);
        }
      }, 2000);
    } catch (_error) {
      setIsLoading(false);
    }
  };

  const resolvedBackgroundColor = useMemo(() => {
    return customBackgroundColor || shade(backgroundColor, 0.7);
  }, [customBackgroundColor, backgroundColor]);

  if (
    !editable &&
    Number(eventDetails?.peopleLimit) &&
    Number(eventDetails?.peopleLimit) <= salesOrderCount
  ) {
    return (
      <HeadlineOnly.V1
        {...props}
        title={{
          id: title.id,
          val: 'Sorry, registration is full'
        }}
        customBackgroundColor={customBackgroundColor || '#fff'}
      />
    );
  }

  return (
    <Container id="form" backgroundColor={resolvedBackgroundColor} className={containerClassname}>
      <Wrapper>
        <FormWrapper>
          <ArrowImg
            width="116"
            height="207"
            viewBox="0 0 116 207"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M108.999 3.96729C110.803 43.4304 119.003 90.8532 102.11 128.412C88.3646 158.972 55.7356 186.515 22.4432 192.856C12.8147 194.69 15.1308 183.137 16.332 177.078C16.9604 173.909 20.9842 159.33 20.8876 160.634C19.5888 178.168 18.0138 188.825 3.44316 199.078C0.778858 200.953 22.2156 203.967 26.9987 203.967"
              stroke="black"
              strokeWidth="6"
              strokeLinecap="round"
              style={{
                stroke: filterBackgroundColor(resolvedBackgroundColor || '#fff', textColor)
              }}
            />
          </ArrowImg>

          <TitleItemForm
            id={title?.id}
            style={{
              color: filterBackgroundColor(oppositeBackgroundColor || '#fff', textColor),
              display: sanitizeRichText(title.val, editable, true) || editable ? 'block' : 'none'
            }}
            onClick={updateActiveElement}
            onBlur={resetActiveElement}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(title.val, editable, true)
                ? sanitizeRichText(title.val, editable, false, '<p>It’s almost yours!</p>')
                : '<p></br><p>'
            }}
            data-accessor-parent={meta['accessorKey']}
            data-accessorkey="title"
            data-placeholder="It’s almost yours!"
            editable={process.env['APP_TYPE'] === 'dashboard'}
            isEmpty={isTitleEmpty}
          />

          <DescriptionTitleForm
            id={content?.id}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(content.val, editable, true)
                ? sanitizeRichText(content.val, editable, false, subHeadline)
                : '<p><br></p>'
            }}
            style={{
              color: filterBackgroundColor(oppositeBackgroundColor || '#fff', textColor),
              display: sanitizeRichText(content.val, editable, true) || editable ? 'block' : 'none'
            }}
            onClick={updateActiveElement}
            onBlur={resetActiveElement}
            data-accessor-parent={meta['accessorKey']}
            data-accessorkey="content"
            data-placeholder={subHeadline}
            editable={process.env['APP_TYPE'] === 'dashboard'}
            isEmpty={isContentEmpty}
          />
          {isVip ? (
            <DescriptionTitleForm>
              The {upsellDetails?.product_name}{' '}
              {TOP_USED_CURRENCIES?.[upsellDetails?.currency as keyof typeof TOP_USED_CURRENCIES] ||
                upsellDetails?.currency}
              {upsellPrice}
            </DescriptionTitleForm>
          ) : null}
          {isLoading ? (
            <Center>
              <CircularProgress color="inherit" />
            </Center>
          ) : !firstPhase && stripePromise ? (
            <Elements stripe={stripePromise} options={{ clientSecret: stripeConfig.clientSecret }}>
              <Second
                orderData={orderData}
                backgroundColor={backgroundColor}
                callback={handleSecond}
                textColor={textColor}
                oppositeBackgroundColor={oppositeBackgroundColor}
              />
            </Elements>
          ) : (
            <First
              backgroundColor={backgroundColor}
              callback={handleFirst}
              textColor={textColor}
              button={button}
              meta={meta}
              setIsVip={setIsVip}
              oppositeBackgroundColor={oppositeBackgroundColor}
            />
          )}
          <FormBg
            style={{
              backgroundColor: oppositeBackgroundColor,
              opacity: oppositeBackgroundColor ? '1' : '0.75'
            }}
          />
        </FormWrapper>
      </Wrapper>
    </Container>
  );
};
